import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, UtensilsCrossed, Phone, MessageCircle } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useLang } from "@/context/LanguageContext";

const links = [
  { to: "/", key: "home" },
  { to: "/services", key: "services" },
  { to: "/gallery", key: "gallery" },
  { to: "/build", key: "build" },
  { to: "/about", key: "about" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const { settings } = useSite();
  const { lang, setLang, t } = useLang();
  const logo = settings?.logo_url;
  const whatsappUrl = "https://wa.me/919923385867?text=Hello%20Mayuresh%20Caterers%2C%20I%20would%20like%20to%20enquire%20about%20catering%20for%20my%20event.";

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 nav-glass transition-all ${scrolled ? "py-2" : "py-4"}`} data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          {logo ? (
            <img src={logo} alt={settings?.business_name || "Mayuresh Caterers"} className="h-11 w-auto object-contain" />
          ) : (
            <>
              <span className="w-9 h-9 flex items-center justify-center bg-[#1A1A1A]"><UtensilsCrossed size={18} className="text-[#C9A66B]" /></span>
              <span className="leading-none"><span className="block serif text-xl font-semibold text-[#1A1A1A]">Mayuresh</span><span className="block label-tag text-[#B08D4F] text-[0.6rem]">Caterers · Roha</span></span>
            </>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => <Link key={l.to} to={l.to} data-testid={`nav-${l.key}`} className={`text-sm font-medium link-underline transition-colors hover:text-[#B08D4F] ${loc.pathname === l.to ? "text-[#B08D4F]" : "text-[#1A1A1A]"}`}>{t(l.key)}</Link>)}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a href="tel:+919923385867" aria-label="Call Mayuresh Caterers" className="w-9 h-9 flex items-center justify-center border border-[#E5DCC9] rounded-sm text-[#1A1A1A] hover:border-[#C9A66B] hover:text-[#B08D4F] transition-colors"><Phone size={16} /></a>
          <a href={whatsappUrl} target="_blank" rel="noreferrer" aria-label="WhatsApp Mayuresh Caterers" className="w-9 h-9 flex items-center justify-center border border-[#E5DCC9] rounded-sm text-[#1A1A1A] hover:border-[#C9A66B] hover:text-[#B08D4F] transition-colors"><MessageCircle size={17} /></a>
          <button onClick={() => setLang(lang === "en" ? "mr" : "en")} data-testid="nav-lang-toggle" className="text-xs border border-[#E5DCC9] rounded-sm px-3 py-2 text-[#1A1A1A] hover:border-[#C9A66B] transition-colors">{lang === "en" ? "मराठी" : "English"}</button>
          <button onClick={() => nav("/build")} className="btn-gold px-5 py-2.5 text-sm rounded-sm" data-testid="nav-cta-quote">{t("quote")}</button>
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label={open ? "Close menu" : "Open menu"}>{open ? <X /> : <Menu />}</button>
      </div>

      {open && (
        <nav className="md:hidden px-5 pt-4 pb-6 flex flex-col gap-4 border-t border-[#E5DCC9] mt-2 bg-[#F9F6F0]">
          {links.map((l) => <Link key={l.to} to={l.to} className="text-base font-medium text-[#1A1A1A]" data-testid={`nav-mobile-${l.key}`}>{t(l.key)}</Link>)}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <a href="tel:+919923385867" className="flex items-center justify-center gap-2 border border-[#E5DCC9] rounded-sm px-3 py-3 text-sm text-[#1A1A1A]"><Phone size={16} /> Call Us</a>
            <a href={whatsappUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 border border-[#E5DCC9] rounded-sm px-3 py-3 text-sm text-[#1A1A1A]"><MessageCircle size={17} /> WhatsApp</a>
          </div>
          <button onClick={() => setLang(lang === "en" ? "mr" : "en")} data-testid="nav-mobile-lang" className="text-sm border border-[#E5DCC9] rounded-sm px-3 py-2 w-fit text-[#1A1A1A]">{lang === "en" ? "मराठी" : "English"}</button>
          <button onClick={() => nav("/build")} className="btn-gold px-5 py-3 text-sm rounded-sm mt-2">{t("quote")}</button>
        </nav>
      )}
    </header>
  );
}
